import { useEffect } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { Card, Spinner } from '@indovation/design-system';
import { useI18n } from '@indovation/i18n/react';
import type { RouteManifestEntry } from '../routes.js';
import { can, hasEntitlement, useSession } from '../auth/session.js';

/**
 * The authentication and entitlement guard.
 *
 * The server decides both — every route re-verifies the token and re-reads the entitlement set,
 * and typing a URL still earns a 401 or a 403. This is here so the UI does not offer what the
 * principal cannot reach, and so an unauthenticated visitor lands on sign-in with their
 * destination preserved rather than on an error.
 */
export default function Guard({ entry, children }: { entry: RouteManifestEntry; children: React.ReactNode }) {
  const { t } = useI18n();
  const { status, me } = useSession();
  const navigate = useNavigate();

  const mustSignIn = entry.authenticated && status === 'anonymous';

  // In an effect, not in the render body. Navigating during render updates the router while
  // React is rendering this component, which React reports as "Cannot update a component while
  // rendering a different component" in every visitor's console.
  useEffect(() => {
    if (!mustSignIn) {
      return;
    }
    const returnTo = `${window.location.pathname}${window.location.search}`;
    void navigate({ to: '/sign-in', search: { returnTo }, replace: true });
  }, [mustSignIn, navigate]);

  if (mustSignIn) {
    return null;
  }

  if (status === 'loading') {
    return <Spinner />;
  }

  // Entitlement first, then permission — layer 2 before layer 3, the same order the API pipeline
  // uses. The messages differ because the remedies differ: one is "your agency has not bought
  // this", which an administrator can change, and the other is "your role does not allow this",
  // which only somebody else can.
  if (!hasEntitlement(me, entry.entitlement)) {
    return (
      <Card title={t('error.unauthorised.title')}>
        {t('error.entitlement.description', { tenant: me?.tenant.name ?? '' })}
      </Card>
    );
  }

  if (entry.requires && !can(me, entry.requires.action, entry.requires.resource)) {
    return (
      <Card title={t('error.unauthorised.title')}>{t('error.unauthorised.description')}</Card>
    );
  }

  return <>{children}</>;
}
